import { useEventRSVP } from "@/src/hooks/features/events/use-event-rsvp.hook";
import { usePermissions } from "@/src/hooks/use-permissions";
import {
  EventItem,
  getMeetingUrl,
  isOnlineEvent,
} from "@/src/models/event.model";
import { format, isToday, isTomorrow } from "date-fns";
import { Location } from "iconsax-react-nativejs";
import GuestEntryModal from "@/src/components/events/guest-entry-modal";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, Pressable, Text, View } from "react-native";
import defaultCover from "../../assets/images/default-group-cover.png";
import ConfirmModal from "../action-modals/confirm-modal";
import ScButton from "../ui-kit/button";
import DropDown, { ContextMenuItem } from "../ui-kit/dropdown";

type EventCardProps = Readonly<{
  event: EventItem;
  showJoin: boolean;
  onPress: (event: EventItem) => void;
  onEdit?: (event: EventItem) => void;
  onDelete?: (event: EventItem) => void;
  onArchive?: (event: EventItem) => void;
  isArchived?: boolean;
  isOwner: boolean;
}>;

const getTimeLabelKey = (
  isoString: string,
): { key: string; options?: Record<string, string> } => {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return { key: "LABEL_DATE_TBD" };
  const time = format(date, "h:mm a");
  if (isToday(date)) {
    const hasStarted = date < new Date();
    return {
      key: hasStarted ? "LABEL_STARTED_AT" : "LABEL_TODAY_AT",
      options: { time },
    };
  }
  if (isTomorrow(date)) return { key: "LABEL_TOMORROW_AT", options: { time } };
  return {
    key: "LABEL_EVENT_DATE",
    options: { date: format(date, "MMM d, yyyy") },
  };
};

const EventCard = ({
  event,
  showJoin,
  onPress,
  onEdit,
  onDelete,
  onArchive,
  isArchived,
  isOwner,
}: EventCardProps) => {
  const { t } = useTranslation();
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);

  const {
    userRsvp,
    attendeeCount,
    isAttending,
    isWaitlisted,
    isFull,
    isJoining,
    isLeaving,
    joinButtonText,
    handleJoin,
    handleLeave,
  } = useEventRSVP(event);

  const handleJoinLeave = () => {
    if (isAttending || isWaitlisted) {
      setShowLeaveModal(true);
    } else {
      // Same guest prompt as the details screen — joining from the listing used
      // to skip it entirely, so guests could only be added one way.
      setShowGuestModal(true);
    }
  };

  const handleConfirmGuests = (guestNames: string[]) => {
    setShowGuestModal(false);
    handleJoin(guestNames);
  };

  const handleConfirmLeave = () => {
    setShowLeaveModal(false);
    if (!userRsvp) return;
    handleLeave(userRsvp.ItemId);
  };

  const timeLabelKey = getTimeLabelKey(event?.startTime);
  const timeLabel = t(timeLabelKey.key, timeLabelKey.options);
  const { canUpdate, canDelete } = usePermissions();
  const canEditEvent = canUpdate("event");
  const canDeleteEvent = canDelete("event");
  const menuItems: ContextMenuItem[] = useMemo(
    () =>
      [
        isOwner &&
          canEditEvent && {
            label: t("LABEL_EDIT"),
            onPress: () => onEdit?.(event),
          },
        {
          label: isArchived ? t("LABEL_UNARCHIVE") : t("LABEL_ARCHIVE"),
          onPress: () => onArchive?.(event),
        },
        isOwner &&
          canDeleteEvent && {
            label: t("LABEL_DELETE"),
            onPress: () => onDelete?.(event),
            destructive: true,
          },
      ].filter(Boolean) as ContextMenuItem[],
    [
      t,
      onEdit,
      onDelete,
      event,
      isArchived,
      onArchive,
      isOwner,
      canEditEvent,
      canDeleteEvent,
    ],
  );
  return (
    <Pressable
      onPress={() => onPress(event)}
      onHoverIn={() => setIsCardHovered(true)}
      onHoverOut={() => setIsCardHovered(false)}
      className={`bg-chalk rounded-2xl overflow-hidden md:min-h-[265px] md:border ${
        isCardHovered ? "md:border-ash-1" : "md:border-ash-3"
      }`}
    >
      <View className="px-2 pt-2">
        <View className="h-32 overflow-hidden rounded-xl">
          <Image
            source={event.coverImage ? { uri: event.coverImage } : defaultCover}
            className="rounded-xl bg-ash-3"
            style={{ width: "100%", height: 128 }}
            resizeMode="cover"
          />
        </View>
      </View>

      {/* Content */}
      <View
        className={`flex-1 px-2 py-2 gap-2 ${showJoin ? "md:py-3 md:gap-1.5 md:justify-between" : "md:py-4 md:gap-4"}`}
      >
        <View className="flex-1 relative flex-row justify-between items-center">
          <Text
            className={`flex-1 text-sm font-bold text-onyx`}
            numberOfLines={showJoin ? 1 : 2}
          >
            {event?.title}
          </Text>

          <DropDown items={menuItems} />
        </View>

        {/* Bottom */}
        <View className={`flex-row items-center `}>
          {/* Location */}
          <View className="gap-1 flex-1">
            {/* CR-B2-10: show online badge or physical location */}
            <View className="flex-row items-center gap-1 overflow-hidden">
              {isOnlineEvent(event) ? (
                <>
                  <View className="px-1.5 py-0.5 rounded-full bg-primary-light">
                    <Text className="text-xs text-primary font-medium">
                      {t("LABEL_ONLINE_EVENT")}
                    </Text>
                  </View>
                  {getMeetingUrl(event) && (
                    <Text
                      className="text-xs shrink min-w-0 text-ash-2"
                      numberOfLines={1}
                      testID="event-card-meeting-url"
                    >
                      {getMeetingUrl(event)}
                    </Text>
                  )}
                </>
              ) : (
                <>
                  <Location size={18} color={"red"} variant="Outline" />
                  <Text
                    className="text-xs shrink min-w-0 text-ash-2"
                    numberOfLines={1}
                  >
                    {event?.locationName}
                  </Text>
                </>
              )}
            </View>
            <View className="flex-row justify-between">
              <Text
                className="text-xs font-semibold text-primary"
                numberOfLines={1}
              >
                {timeLabel}
              </Text>
              {showJoin && event.capacity && (
                <View className="flex-row items-center gap-1.5">
                  <View className="w-12 h-1 rounded-full bg-ash-3 overflow-hidden">
                    <View
                      className={`h-full rounded-full ${isFull ? "bg-red-400" : "bg-primary"}`}
                      style={{
                        width: `${Math.min((attendeeCount / event.capacity) * 100, 100)}%`,
                      }}
                    />
                  </View>
                  <Text
                    className={`text-xs ${isFull ? "text-red-400" : "text-slate"}`}
                  >
                    {attendeeCount}/{event.capacity}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
        {showJoin && (
          <ScButton
            text={joinButtonText}
            variant="pill"
            loading={isJoining || isLeaving}
            onPress={handleJoinLeave}
          />
        )}
      </View>
      <GuestEntryModal
        visible={showGuestModal}
        isSubmitting={isJoining}
        onCancel={() => setShowGuestModal(false)}
        onConfirm={handleConfirmGuests}
      />
      <ConfirmModal
        visible={showLeaveModal}
        title={
          isWaitlisted
            ? "LABEL_LEAVE_WAITLIST_CONFIRM"
            : "LABEL_LEAVE_EVENT_CONFIRM"
        }
        description={
          isWaitlisted
            ? "HINT_LEAVE_WAITLIST_CONFIRM"
            : "HINT_LEAVE_EVENT_CONFIRM"
        }
        confirmText={t(isWaitlisted ? "BTN_LEAVE_WAITLIST" : "BTN_LEAVE")}
        cancelText={t("BTN_CANCEL")}
        onCancel={() => setShowLeaveModal(false)}
        onConfirm={handleConfirmLeave}
      />
    </Pressable>
  );
};

export default EventCard;
